import { use, useMemo, useRef, useState } from "react";
import clsx from "clsx";

import { useTranslation } from "lib/i18n/use-translation";
import { ModalsContext } from "lib/modals/use-modals";
import { NavigationContext } from "lib/navigation/use-navigation";
import { usePaths } from "lib/navigation/use-paths";
import { NotificationsContext } from "lib/notifications/use-notifications";
import { type Quote, type QuoteColor } from "lib/quotes/get-quotes";
import { useQuotes } from "lib/quotes/use-quotes";
import { ColorSwatches } from "lib/ui/color-swatches";
import { FloatingMenu, useFloatingMenu } from "lib/ui/floating-menu";

import DotsIcon from "assets/icons/dotsburger.svg?react";
import ShareIcon from "assets/icons/share.svg?react";
import TrashIcon from "assets/icons/trash.svg?react";
import css from "./quotes.module.scss";

type QuoteProps = {
  quote: Quote;
  onClick: () => void;
  onDelete: () => void;
  disabled?: boolean;
};

function QuoteItem({ quote, onClick, onDelete, disabled }: QuoteProps) {
  const { t } = useTranslation();
  const { updateQuote } = useQuotes();
  const { setBookmarksPanel, openShareQuote } = use(ModalsContext)!;
  const { showToast } = use(NotificationsContext)!;
  const triggerRef = useRef<HTMLDivElement>(null);
  const actionsMenu = useFloatingMenu(triggerRef);

  const colorClass = quote.color || "basic";

  function handleShare() {
    actionsMenu.close();
    if (quote.quote_text.length < 30) {
      showToast(t("Цитата слишком короткая"), {
        message: t("Минимальная длина цитаты для публикации — 30 символов"),
      });
      return;
    }
    openShareQuote(quote.id, quote.quote_text);
  }

  function handleDelete() {
    onDelete();
    actionsMenu.close();
  }

  async function handleColorSelect(color: QuoteColor) {
    const [, error] = await updateQuote(quote.id, { color });
    if (error) {
      setBookmarksPanel(prev => ({ ...prev, isOpen: false }));
      showToast(t("Не удалось изменить цитату"), {
        message: t("Пожалуйста, обновите страницу и попробуйте снова"),
      });
    }
    actionsMenu.close();
  }

  return (
    <div className={clsx(css.card, `quote-${colorClass}`)}>
      <div className={css.quote}>
        <button className={css.quoteText} onClick={onClick} disabled={disabled}>
          {quote.quote_text}
        </button>
        <div ref={triggerRef} className={css.menuContainer}>
          <button
            className={css.menuButton}
            onClick={e => {
              e.stopPropagation();
              actionsMenu.toggle();
            }}
            title={t("Меню")}
          >
            <DotsIcon />
          </button>
        </div>
      </div>
      {actionsMenu.anchor && (
        <FloatingMenu anchor={actionsMenu.anchor} onClose={actionsMenu.close}>
          <ColorSwatches
            selected={quote.color as QuoteColor}
            onSelect={handleColorSelect}
          />
          <button
            className={css.menuItem}
            onClick={e => {
              e.stopPropagation();
              handleShare();
            }}
          >
            <ShareIcon />
            {t("Поделиться")}
          </button>
          <button
            className={clsx(css.menuItem, css.danger)}
            onClick={e => {
              e.stopPropagation();
              handleDelete();
            }}
          >
            <TrashIcon />
            {t("Удалить")}
          </button>
        </FloatingMenu>
      )}
      <NoteField quote={quote} />
    </div>
  );
}

type NoteFieldProps = {
  quote: Quote;
};

function NoteField({ quote }: NoteFieldProps) {
  const { t } = useTranslation();
  const { updateQuote } = useQuotes();
  const { setBookmarksPanel } = use(ModalsContext)!;
  const { showToast } = use(NotificationsContext)!;
  const [noteText, setNoteText] = useState(quote.note_text || "");
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasNote = noteText.trim().length > 0;

  const handleBlur = async () => {
    const trimmedNote = noteText.trim();
    if (trimmedNote !== (quote.note_text || "")) {
      setIsSaving(true);
      const [, error] = await updateQuote(quote.id, {
        note_text: trimmedNote || undefined,
      });
      if (error) {
        setBookmarksPanel(prev => ({ ...prev, isOpen: false }));
        showToast(t("Не удалось сохранить заметку"), {
          message: t("Пожалуйста, обновите страницу и попробуйте снова"),
        });
      }
      setIsSaving(false);
    }
  };

  return (
    <div className={css.noteField}>
      <textarea
        ref={textareaRef}
        className={clsx(css.noteInput, { [css.filled]: hasNote })}
        placeholder={t("Заметка")}
        value={noteText}
        onChange={e => setNoteText(e.target.value)}
        onBlur={handleBlur}
        disabled={isSaving}
        rows={1}
      />
    </div>
  );
}

export function Quotes() {
  const { quotes, deleteQuote } = useQuotes();
  const { t } = useTranslation();
  const { showToast } = use(NotificationsContext)!;
  const { updateBookmark } = use(NavigationContext)!;
  const { xpointerToChapter, xpointerToIndex, context } = usePaths();
  const { bookmarksPanel, setBookmarksPanel } = use(ModalsContext)!;
  const { selectedQuoteId } = bookmarksPanel;

  function handleQuoteClick(xpointer: string) {
    updateBookmark(xpointer);
    setBookmarksPanel(prev => ({ ...prev, isOpen: false }));
  }

  async function handleDeleteQuote(quoteId: string) {
    const [, error] = await deleteQuote(quoteId);
    if (error) {
      setBookmarksPanel(prev => ({ ...prev, isOpen: false }));
      showToast(t("Не удалось удалить цитату"), {
        message: t("Пожалуйста, обновите страницу и попробуйте снова"),
      });
    }
  }

  function scrollToQuoteRef(el: HTMLDivElement | null) {
    if (el) {
      el.scrollIntoView({ behavior: "instant", block: "center" });
      el.classList.add(css.highlight);
      el.addEventListener(
        "animationend",
        () => el.classList.remove(css.highlight),
        { once: true }
      );
      setBookmarksPanel(prev => ({ ...prev, selectedQuoteId: null }));
    }
  }

  const groupedQuotes = useMemo(() => {
    const groups = new Map<string, Quote[]>();

    quotes.forEach(quote => {
      const chapterTitle =
        xpointerToChapter(quote.start_xpointer)?.t || t("Без заголовка");
      if (!groups.has(chapterTitle)) {
        groups.set(chapterTitle, []);
      }
      groups.get(chapterTitle)!.push(quote);
    });

    return Array.from(groups.entries());
  }, [quotes, t, xpointerToChapter]);

  return (
    <div className={css.content}>
      {quotes.length > 0 ? (
        <div className={css.list}>
          {groupedQuotes.map(([chapterTitle, chapterQuotes]) => (
            <div key={chapterTitle} className={css.chapterGroup}>
              {chapterQuotes.map((quote, index) => (
                <div
                  key={quote.id}
                  className={css.quoteItem}
                  ref={quote.id === selectedQuoteId ? scrollToQuoteRef : null}
                >
                  {index === 0 && (
                    <div className={css.chapterTitle}>{chapterTitle}</div>
                  )}
                  <QuoteItem
                    quote={quote}
                    onClick={() => handleQuoteClick(quote.start_xpointer)}
                    onDelete={() => handleDeleteQuote(quote.id)}
                    disabled={(() => {
                      const idx = xpointerToIndex(quote.start_xpointer);
                      return (
                        idx === null ||
                        context.layout?.elements[idx] === undefined
                      );
                    })()}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div>{t("Нет цитат")}</div>
      )}
    </div>
  );
}
